import React, { useEffect, useState, createContext, useContext } from 'react';
type User = {
  id: string;
  email: string;
  role: string;
  name: string;
  isAdmin: boolean;
};
type AuthContextType = {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string, role: string) => Promise<boolean>;
  logout: () => void;
};
const AuthContext = createContext<AuthContextType | undefined>(undefined);
// Mock user data for demonstration
const MOCK_USERS = [{
  id: '1',
  email: 'contractor@example.com',
  password: 'password',
  role: 'contractor',
  name: 'John Contractor',
  isAdmin: false
}, {
  id: '2',
  email: 'broker@example.com',
  password: 'password',
  role: 'broker',
  name: 'Jane Broker',
  isAdmin: false
}, {
  id: '3',
  email: 'foreman@example.com',
  password: 'password',
  role: 'foreman',
  name: 'Mike Foreman',
  isAdmin: false
}, {
  id: '4',
  email: 'trucking@example.com',
  password: 'password',
  role: 'trucking',
  name: 'Tom Trucking',
  isAdmin: false
}, {
  id: '5',
  email: 'driver@example.com',
  password: 'password',
  role: 'driver',
  name: 'Dave Driver',
  isAdmin: false
}, {
  id: '6',
  email: 'factoring@example.com',
  password: 'password',
  role: 'factoring',
  name: 'Fiona Factoring',
  isAdmin: false
}, {
  id: '7',
  email: 'admin@example.com',
  password: 'password',
  role: 'admin',
  name: 'Admin User',
  isAdmin: true
}, {
  id: '8',
  email: 'dirtlogics',
  password: 'password123',
  role: 'admin',
  name: 'Dirt Logics Admin',
  isAdmin: true
}, {
  id: '9',
  email: 'dirtlogics',
  password: 'password123',
  role: 'contractor',
  name: 'Dirt Logics Contractor',
  isAdmin: true
}, {
  id: '10',
  email: 'dirtlogics',
  password: 'password123',
  role: 'broker',
  name: 'Dirt Logics Broker',
  isAdmin: true
}, {
  id: '11',
  email: 'dirtlogics',
  password: 'password123',
  role: 'foreman',
  name: 'Dirt Logics Foreman',
  isAdmin: true
}, {
  id: '12',
  email: 'dirtlogics',
  password: 'password123',
  role: 'trucking',
  name: 'Dirt Logics Trucking',
  isAdmin: true
}, {
  id: '13',
  email: 'dirtlogics',
  password: 'password123',
  role: 'driver',
  name: 'Dirt Logics Driver',
  isAdmin: true
}, {
  id: '14',
  email: 'dirtlogics',
  password: 'password123',
  role: 'factoring',
  name: 'Dirt Logics Factoring',
  isAdmin: true
}];
export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  useEffect(() => {
    // Clear any stale auth data from previous versions
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        // Verify the saved user still exists in MOCK_USERS
        const stillValid = MOCK_USERS.find(u => u.id === parsedUser.id && u.email.toLowerCase() === parsedUser.email.toLowerCase());
        if (stillValid) {
          setUser(parsedUser);
          setIsAuthenticated(true);
        } else {
          // Clear stale session
          localStorage.removeItem('user');
          localStorage.removeItem('rememberedEmail');
          localStorage.removeItem('rememberedRole');
        }
      } catch (error) {
        console.error('Failed to parse saved user', error);
        localStorage.removeItem('user');
      }
    }
  }, []);
  const login = async (email: string, password: string, role: string): Promise<boolean> => {
    // In a real app, this would be an API call
    const foundUser = MOCK_USERS.find(u => u.email.toLowerCase() === email.toLowerCase() && u.password === password && u.role === role);
    if (foundUser) {
      const {
        password: _,
        ...userWithoutPassword
      } = foundUser;
      setUser(userWithoutPassword);
      setIsAuthenticated(true);
      localStorage.setItem('user', JSON.stringify(userWithoutPassword));
      return true;
    }
    return false;
  };
  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
    localStorage.removeItem('rememberedEmail');
    localStorage.removeItem('rememberedRole');
  };
  return <AuthContext.Provider value={{
    user,
    isAuthenticated,
    login,
    logout
  }} data-id="element-41">
      {children}
    </AuthContext.Provider>;
};
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};